import React, { useState, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Toaster } from "sonner";
import { initAnalytics, trackPageView } from "@/lib/analytics";

import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import NewsletterPopup from "@/components/NewsletterPopup";

// Critical: HomePage loads eagerly for fastest first paint
import HomePage from "@/pages/HomePage";

// Lazy-load everything else — loaded on demand when user navigates
const CollectionsPage = lazy(() => import("@/pages/CollectionsPage"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const WishlistPage = lazy(() => import("@/pages/WishlistPage"));
const BrandPage = lazy(() => import("@/pages/BrandPage"));
const RewardsPage = lazy(() => import("@/pages/RewardsPage"));
const SkinAnalysisPage = lazy(() => import("@/pages/SkinAnalysisPage"));
const OurStoryPage = lazy(() => import("@/pages/OurStoryPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ShippingReturnsPage = lazy(() => import("@/pages/ShippingReturnsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const TrackOrderPage = lazy(() => import("@/pages/TrackOrderPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboard"));
const OrderConfirmationPage = lazy(() => import("@/pages/OrderConfirmationPage"));
const AIAdvisor = lazy(() => import("@/components/AIAdvisor"));
const BeautyQuiz = lazy(() => import("@/components/BeautyQuiz"));
const JournalPages = lazy(() => import("@/pages/Journal").then(m => ({ default: m.JournalIndex })));
const JournalArticlePage = lazy(() => import("@/pages/Journal").then(m => ({ default: m.JournalArticle })));

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <p className="font-sans text-xs tracking-[0.2em] uppercase text-[#333333]/30 animate-pulse">Loading...</p>
    </div>
  );
}

const SHOPIFY_CHECKOUT_DOMAIN = "mvq580-mi.myshopify.com";

function ShopifyCheckoutFallback() {
  const checkoutPath = window.location.pathname + window.location.search;
  const shopifyUrl = `https://${SHOPIFY_CHECKOUT_DOMAIN}${checkoutPath}`;
  const [showFallback, setShowFallback] = React.useState(false);

  React.useEffect(() => {
    // Detect redirect loop: if we were redirected here within 10s, Shopify is looping us
    try {
      const lastAttempt = parseInt(sessionStorage.getItem("doria_checkout_ts") || "0", 10);
      const now = Date.now();
      if (now - lastAttempt < 15000) {
        // Loop detected — show manual checkout
        setShowFallback(true);
        return;
      }
      // First attempt — try redirect to Shopify
      sessionStorage.setItem("doria_checkout_ts", now.toString());
      window.location.replace(shopifyUrl);
    } catch {
      setShowFallback(true);
    }
  }, [shopifyUrl]);

  if (!showFallback) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="font-sans text-sm text-[#333333]/50 animate-pulse">Redirecting to checkout...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-20 bg-[#F9F8F6]" data-testid="checkout-fallback">
      <div className="max-w-md w-full text-center">
        <p className="font-serif text-3xl tracking-tight text-[#333333]">Almost <span className="italic">there</span></p>
        <p className="font-sans text-sm text-[#333333]/55 mt-4 leading-relaxed">
          Your bag is ready. Click below to complete your purchase on our secure Shopify checkout.
        </p>
        <a
          href={shopifyUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="manual-checkout-btn"
          className="mt-8 inline-flex items-center gap-2 bg-[#333333] text-[#F9F8F6] px-10 py-4 text-[11px] tracking-[0.25em] uppercase font-sans font-semibold hover:bg-[#DCAE96] hover:text-[#333333] transition-colors duration-300"
        >
          Complete checkout
        </a>
        <p className="font-sans text-xs text-[#333333]/30 mt-6">Secure checkout powered by Shopify</p>
      </div>
    </div>
  );
}

function AppShell() {
  const [aiOpen, setAiOpen] = useState(false);
  const [aiProduct, setAiProduct] = useState(null);
  const [aiAutoMessage, setAiAutoMessage] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => { trackPageView(location.pathname, document.title); }, [location]);
  useEffect(() => {
    if (navType !== "POP" && !location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash, navType]);

  // Clear product context when leaving product pages
  useEffect(() => {
    if (!location.pathname.startsWith("/product/")) {
      window.__doriaProduct = null;
      setAiProduct(null);
    }
  }, [location]);

  // Listen for "Build my ritual" events from ProductPage
  useEffect(() => {
    const handler = (e) => {
      const name = e.detail?.productName || "this product";
      const product = window.__doriaProduct || null;
      setAiProduct(product);
      setAiAutoMessage(`I'm interested in ${name}. How should I build my ritual around this product?`);
      setAiOpen(true);
    };
    window.addEventListener("openDoriaRitual", handler);
    return () => window.removeEventListener("openDoriaRitual", handler);
  }, []);

  const openAI = () => {
    const product = window.__doriaProduct || null;
    setAiProduct(product);
    setAiAutoMessage(null);
    setAiOpen(true);
  };

  return (
    <>
      <Header onOpenAI={openAI} onOpenQuiz={() => setQuizOpen(true)} />
      <main>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage onOpenAI={openAI} onOpenQuiz={() => setQuizOpen(true)} />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/shop/:category" element={<ShopPage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/brand/:brand" element={<BrandPage />} />
          <Route path="/journal" element={<JournalPages />} />
          <Route path="/journal/:slug" element={<JournalArticlePage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/skin-analysis" element={<SkinAnalysisPage />} />
          <Route path="/our-story" element={<Navigate to="/about" replace />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faqs" element={<FAQPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/track-order" element={<TrackOrderPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
          <Route path="/cart/c/*" element={<ShopifyCheckoutFallback />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <CartDrawer />
      <Suspense fallback={null}>
        <AIAdvisor open={aiOpen} onClose={() => { setAiOpen(false); setAiProduct(null); setAiAutoMessage(null); }} productContext={aiProduct} autoMessage={aiAutoMessage} />
        <BeautyQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
      </Suspense>

      {!aiOpen && (
        <button
          onClick={openAI}
          data-testid="floating-ai-btn"
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 bg-obsidian text-bone w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-lg hover:bg-neutral-800 transition-colors flex items-center justify-center"
          aria-label="Open AI advisor"
        >
          <Sparkles className="w-5 h-5" />
        </button>
      )}
      <Toaster position="top-center" />
      <NewsletterPopup />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <WishlistProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </WishlistProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
